/**
 * SourcesSidebar Component
 *
 * Sidebar for multi-source mode (e.g., Routing Rules with CRM data).
 * Shows available data sources like Analysis, CRM Deal, CRM Lead, etc.
 */

import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { SourcesSidebarProps } from './types';

export function SourcesSidebar({
  sources,
  selectedSourceId,
  onSelectSource,
}: SourcesSidebarProps) {
  const { t } = useTranslation('common');
  if (sources.length <= 1) {
    return null;
  }

  return (
    <div className="w-[140px] border-r border-border bg-bg-surface flex-shrink-0">
      <div className="px-2 py-2">
        <p className="px-2 text-xs font-semibold text-fg-muted uppercase tracking-wider mb-1">
          {t('fieldPicker.sources', 'Sources')}
        </p>
        <nav
          className="space-y-0.5"
          role="tablist"
          aria-label={t('fieldPicker.fieldSources', 'Field sources')}
        >
          {sources.map((source) => {
            const isSelected = source.id === selectedSourceId;

            return (
              <button
                key={source.id}
                type="button"
                onClick={() => onSelectSource(source.id)}
                role="tab"
                aria-selected={isSelected}
                aria-controls={`source-panel-${source.id}`}
                className={cn(
                  'w-full flex items-center gap-2 px-2 py-2 text-left text-sm',
                  'rounded transition-colors',
                  'focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                  isSelected
                    ? 'bg-primary-dim text-primary-fg font-medium'
                    : 'text-fg-muted hover:text-fg hover:bg-bg-muted'
                )}
              >
                {source.icon && (
                  <span className="flex-shrink-0 w-4 h-4">{source.icon}</span>
                )}
                <span className="truncate">{source.label}</span>
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );
}

export default SourcesSidebar;
